import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import BubbleSort from "../Algorithms/BubbleSort";
import InsertionSort from "../Algorithms/InsertionSort";
import QuickSort from "../Algorithms/QuickSort";
import MergeSort from "../Algorithms/MergeSort";
import SelectionSort from "../Algorithms/SelectionSort";
import HeapSort from "../Algorithms/HeapSort";
import BucketSort from "../Algorithms/BucketSort";
import { bubbleSortCode } from "../displayAlgo/Bubble";
import { bucketSortCode } from "../displayAlgo/Bucket";
import { heapSortCode } from "../displayAlgo/Heap";
import { insertionSortCode } from "../displayAlgo/Insertion";
import { mergeSortCode } from "../displayAlgo/Merge";
import { quickSortCode } from "../displayAlgo/Quick";
import { selectionSortCode } from "../displayAlgo/Selection";
import "../styles/Visuals.css";
import Button from "../../../components/shared/Button";
import {
  playPause,
  changeValues as changeValuesAction,
} from "../../../redux/slices/visualSlice";

export default function Visuals() {
  const myState = useSelector((state) => state.updateProps);
  const dispatch = useDispatch();
  const color = myState.color;
  const range = myState.range;
  const [iterations, setIterations] = useState(0);
  const [complexity, setComplexity] = useState({ time: "", space: "" });
  const [showComplexityPopup, setShowComplexityPopup] = useState(false);
  const [showAlgorithmPopup, setShowAlgorithmPopup] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("python");
  const [copySuccess, setCopySuccess] = useState("");
  const [isSorting, setIsSorting] = useState(false);

  const countIterations = () => {
    setIterations((prev) => prev + 1);
  };

  const changeValues = () => {
    dispatch(changeValuesAction());
    setIterations(0);
    setComplexity({ time: "", space: "" });
    calculateComplexity();
  };

  const handlePlayPause = (play) => {
    if (!myState.play) {
      setIsSorting(true);
      dispatch(playPause(play));
    }
  };

  useEffect(() => {
    calculateComplexity();
  }, [myState.algorithm]);

  // Re-enable buttons when sorting finishes
  useEffect(() => {
    if (!myState.play) {
      setIsSorting(false);
    }
  }, [myState.play]);

  const calculateComplexity = () => {
    let timeComplexity, spaceComplexity;
    switch (myState.algorithm) {
      case "bubble":
      case "insertion":
      case "selection":
        timeComplexity = (<>O(n<sup>2</sup>)</>);
        spaceComplexity = <>O(1)</>;
        break;
      case "quick":
        timeComplexity = <>O(n log n)</>;
        spaceComplexity = <>O(log n)</>;
        break;
      case "merge":
        timeComplexity = <>O(n log n)</>;
        spaceComplexity = <>O(n)</>;
        break;
      case "heap":
        timeComplexity = <>O(n log n)</>;
        spaceComplexity = <>O(1)</>;
        break;
      case "bucket":
        timeComplexity = <>O(n + k)</>;
        spaceComplexity = <>O(n)</>;
        break;
      default:
        timeComplexity = <>Unknown</>;
        spaceComplexity = <>Unknown</>;
    }
    setComplexity({ time: timeComplexity, space: spaceComplexity });
  };

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
    setCopySuccess("");
  };

  const getAlgorithmCode = () => {
    switch (myState.algorithm) {
      case "bubble":    return bubbleSortCode[selectedLanguage];
      case "bucket":    return bucketSortCode[selectedLanguage];
      case "heap":      return heapSortCode[selectedLanguage];
      case "insertion": return insertionSortCode[selectedLanguage];
      case "merge":     return mergeSortCode[selectedLanguage];
      case "quick":     return quickSortCode[selectedLanguage];
      case "selection": return selectionSortCode[selectedLanguage];
      default:          return "Algorithm code not available";
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getAlgorithmCode());
      setCopySuccess("Copied!");
      setTimeout(() => setCopySuccess(""), 2000);
    } catch (err) {
      setCopySuccess("Failed to copy");
    }
  };

  const speed = myState.speed;

  return (
    <div className="visuals">
      <div className="visualizer">
        {myState.algorithm === "quick" && (
          <div className="legend">
            <div className="legend__label"></div> Pivot elements
          </div>
        )}
        <div
          className="visual__items"
          style={{ width: `${myState.values.length * 11}px` }}
        >
          {myState.values.map((item) => (
            <div
              className="visual__item"
              key={item[1]}
              id={item[1]}
              style={{
                transition: `${speed / 1000}s linear all`,
                transform: `translateX(${item[1] * 11}px)`,
              }}
            >
              <h4>{item[0]}</h4>
              <div
                className="visual"
                style={{
                  height: `${item[0] * 3}px`,
                  backgroundColor: color,
                  width: range < 35 ? "8px" : "6px",
                }}
              ></div>
            </div>
          ))}
        </div>
      </div>

      <div className="visual__btns">
        <Button id="change-btn" disabled={isSorting} onClick={changeValues}>Change Values</Button>
        <Button id="play-btn" disabled={isSorting} onClick={() => handlePlayPause(true)}>Play</Button>
      </div>

      <div className="visual__btns">
        <Button
          onClick={() => {
            setShowComplexityPopup(true);
            setShowAlgorithmPopup(false);
          }}
        >
          Show Complexities
        </Button>
        <Button
          onClick={() => {
            setShowAlgorithmPopup(true);
            setShowComplexityPopup(false);
          }}
        >
          Show Algorithm
        </Button>
      </div>

      <div className="iteration-counter">Iterations: {iterations}</div>

      {showComplexityPopup && (
        <div className="complexity-popup">
          <div className="Spopup-content">
            <h3>Algorithm Complexities</h3>
            <p>Time Complexity: {complexity.time}</p>
            <p>Space Complexity: {complexity.space}</p>
            <Button variant="secondary" size="sm" onClick={() => setShowComplexityPopup(false)}>Close</Button>
          </div>
        </div>
      )}

      {showAlgorithmPopup && (
        <div className="algorithm-popup">
          <div className="Spopup-content">
            <h3>
              Algorithm Code:{" "}
              {myState.algorithm.charAt(0).toUpperCase() + myState.algorithm.slice(1)} Sort
            </h3>
            <select value={selectedLanguage} onChange={handleLanguageChange}>
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="cpp">C++</option>
              <option value="c">C</option>
            </select>
            <pre>{getAlgorithmCode()}</pre>
            <div className="button-group">
              <Button variant="secondary" onClick={copyToClipboard}>{copySuccess || "Copy Code"}</Button>
              <Button variant="secondary" size="sm" onClick={() => setShowAlgorithmPopup(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      <BubbleSort countIterations={countIterations} />
      <InsertionSort countIterations={countIterations} />
      <MergeSort countIterations={countIterations} />
      <QuickSort countIterations={countIterations} />
      <SelectionSort countIterations={countIterations} />
      <BucketSort countIterations={countIterations} />
      <HeapSort countIterations={countIterations} />
    </div>
  );
}
